import { useMutation, UseMutationOptions, useQuery } from "@tanstack/react-query";
import { RecipeIngredientType } from "../config/enums/IngredientType.enum";
import { RecipeType, RecipeTypeClassification } from "../config/enums/recipeSimulator.enum";
import { successMessage } from "../helpers/ErrorHandler";
import { ApiServiceErr, axiosApi } from "./apiService";
import { RecipeResult } from "./recipeResults";

interface CalculatedProductSalesUnitDto {
  _id?: string;
  productId: string;
  salesUnitId: string;
  percentage: number;
  chartColor?: string;
}

interface GenericIngredientDto {
  _id?: string;
  ingredientId: string;
  percentage: number;
  chartColor?: string;
}

interface CalculatedIngredientDto {
  _id?: string;
  ingredientId: string;
  supplierId: string;
  percentage: number;
  chartColor?: string;
}

interface ProcessDto {
  _id?: string;
  processName: string;
  input: number | null;
  output: number | null;
  country: number;
  waste?: number;
  wasteUnit?: number;
  sideStream?: number | null;
  sideStreamUnit?: number;
  electricity?: number | null;
  electricityUnit?: number;
  wastewater?: number | null;
  wastewaterUnit?: number;
  heat?: number | null;
  heatUnit?: number;
  processNote?: string;
  chartColor?: string;
  // Process relationship tracking fields (for duplicated processes)
  originalRecipeId?: string;
  originalProcessId?: string;
  recipeTypeClassification?: RecipeTypeClassification;
}

interface VariantDto {
  _id?: string;
  variantName: string;
  notes?: string;
  calculatedProductSalesUnit?: CalculatedProductSalesUnitDto[];
  genericIngredient?: GenericIngredientDto[];
  calculatedIngredient?: CalculatedIngredientDto[];
  processes?: ProcessDto[];
}

interface CreateRecipeDto {
  recipeName: string;
  notes?: string;
}

interface CreateRecipeFromDemoKitDto {
  recipeName: string;
  notes: string;
}

interface CreateRecipeFromDemoKitParams {
  demoKitId: string;
  recipe: CreateRecipeFromDemoKitDto;
}

interface CalculatedProductSalesUnit {
  productId: string;
  salesUnitId: string;
  percentage: number;
}

interface GenericIngredient {
  ingredientId: string;
  country: string;
  percentage: number;
}

interface CalculatedIngredient {
  ingredientId: string;
  supplierId: string;
  percentage: number;
}

export interface Process {
  _id?: string;
  processName: string;
  dataType?: string;
  genericProcessId?: number | null;
  title?: string;
  input: number | null;
  inputUnit?: number;
  output: number | null;
  outputUnit?: number;
  country: number;
  waste?: number;
  wasteUnit?: number;
  sideStream?: number | null;
  sideStreamUnit?: number;
  electricity?: number | null;
  electricityUnit?: number;
  wastewater?: number | null;
  wastewaterUnit?: number;
  heat?: number | null;
  heatUnit?: number;
  processNote?: string;
  chartColor?: string;
  // Semi-specific macronutrient fields
  water?: number;
  carbohydrates?: number;
  protein?: number;
  fat?: number;
  ashMinerals?: number;
  starch?: number;
  hydrocolloidsGums?: number;
  fiber?: number;
  gellingProtein?: number;
  gelatin?: number;
  solidFat?: number;
  startingTemperature?: number;
  startingTemperatureUnit?: string;
  targetTemperature?: number;
  targetTemperatureUnit?: string;
  maintenanceTime?: number;
  // Heating/Cooling mode fields
  processDirection?: string;
  coolingMethod?: number | null;
  durationValue?: number;
  durationType?: string;
  extendedStorage?: boolean;
  // Process relationship tracking fields
  originalRecipeId?: string;
  originalProcessId?: string;
  recipeTypeClassification?: RecipeTypeClassification;
}

export type { ProcessFormValues } from "../helpers/ProcessPayloadHelper";

export interface Variant {
  _id: string;
  variantName: string;
  notes: string;
  calculatedProductSalesUnit: CalculatedProductSalesUnit[];
  genericIngredient: GenericIngredient[];
  calculatedIngredient: CalculatedIngredient[];
  processes: Process[];
}

interface AddProcess {
  recipeId: string;
  variantId: string;
  processes: Process[];
}

interface UpdateProcess {
  recipeId: string;
  variantId: string;
  processId: string;
  recipeType: RecipeType;
  process: Process;
}

export interface DeleteProcess {
  recipeId: string;
  processId: string;
  variantId?: string;
  recipeType: RecipeType;
}

export interface RecipeData {
  _id: string;
  tenantId: string;
  recipeName: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  demoKitDetails?: {
    demoKitName?: string;
    isDemoKitDeleted?: boolean;
    demoKitDeletedAt?: string;
  };
  // Optional recipe-level type classification (for future use when recipes have types)
  recipeTypeClassification?: RecipeTypeClassification;
  variants: any[];
  originalRecipe: {
    calculatedProductSalesUnit: {
      _id: string;
      productId: string;
      productName: string;
      salesUnitId: string;
      salesUnitName: string;
      percentage: number;
      chartColor?: string;
    }[];
    genericIngredient: {
      _id: string;
      ingredientId: number;
      ingredientName: string;
      percentage: number;
      chartColor?: string;
    }[];
    calculatedIngredient: {
      _id: string;
      ingredientId: string;
      supplierId: string;
      ingredientName: string;
      percentage: number;
      chartColor?: string;
    }[];
    isUpdated?: boolean;
    processes: any[];
    _id: string;
  };
}

export interface RecipeCreatedBy {
  id: string;
  firstName?: string;
  lastName?: string;
}

export type RecipeCreatedByValue = string | RecipeCreatedBy;

export interface Recipe {
  _id: string;
  recipeName: string;
  notes?: string;
  recipeTypeClassification?: RecipeTypeClassification;
  originalRecipe: {
    calculatedProductSalesUnit: any[];
    genericIngredient: any[];
    calculatedIngredient: any[];
    processes: any[];
  };
  originalRecipeclimateChange?: number;
  variants: {
    _id: string;
    variantName: string;
    notes?: string;
    calculatedProductSalesUnit: any[];
    genericIngredient: any[];
    calculatedIngredient: any[];
    processes: any[];
    variantclimateChange?: number;
  }[];
  updatedAt?: string | null;
  calculatedAt?: string | null;
  status?: string;
  createdBy?: RecipeCreatedByValue;
  createdById?: string;
  createdByName?: string;
  userFirstName?: string;
  userLastName?: string;
  isOwner?: boolean;
}

interface AddOriginalIngredientDto {
  recipeId: string;
  type: RecipeIngredientType;
  data: CalculatedProductSalesUnitDto | GenericIngredientDto | CalculatedIngredientDto;
}

interface UpdateOriginalIngredientDto extends AddOriginalIngredientDto {
  id: string;
}

interface DeleteRecipeIngredientDto {
  recipeId: string;
  id: string;
  type: RecipeIngredientType;
}

interface AddVariant {
  recipeId: string;
  variant: VariantDto;
}

interface UpdateVariant {
  recipeId: string;
  variantId: string;
  variant: VariantDto;
}

interface UpdateRecipe {
  recipeId: string;
  recipe: CreateRecipeDto;
}

interface UpdateIngredientPercentageDto {
  recipeId: string;
  ingredientId: string;
  type: RecipeIngredientType;
  percentage: number;
  variantId?: string;
}

export const useCreateRecipe = (opt?: UseMutationOptions<Recipe, ApiServiceErr, CreateRecipeDto>) =>
  useMutation<Recipe, ApiServiceErr, CreateRecipeDto>(
    async data => {
      const response = await axiosApi.post("/recipe", data);
      successMessage("Recipe created successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useCreateRecipeFromDemoKit = (
  opt?: UseMutationOptions<Recipe, ApiServiceErr, CreateRecipeFromDemoKitParams>
) =>
  useMutation<Recipe, ApiServiceErr, CreateRecipeFromDemoKitParams>(
    async ({ demoKitId, recipe }) => {
      const response = await axiosApi.post(`/recipe/from-demo-kit/${demoKitId}`, recipe);
      successMessage("Recipe created successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useGetRecipeList = (pageNumber: number, pageLimit: number, searchKey: string) =>
  useQuery(
    ["useGetRecipeList", pageNumber, pageLimit, searchKey],
    async () => {
      const response = await axiosApi.get(
        `/recipe?pageNumber=${pageNumber}&pageSize=${pageLimit}&searchKey=${searchKey}`
      );
      return response?.data || [];
    },
    { keepPreviousData: true }
  );

export const NONE_DEMO_KIT_VALUE = "none";

export interface DemoKitOption {
  id: string;
  title: string;
  value: string;
}

interface DemoKitRecipe {
  demoKitId: string;
  recipeName: string;
}

interface DemoKitUsageItem {
  recipeName?: string;
  createdBy?: string;
  createdByName?: string;
  createdDate?: string;
  createdAt?: string;
  copiedFromDemoKitAt?: string;
  userFirstName?: string;
  userLastName?: string;
}

export interface DemoKitUsageRecord {
  recipeName: string;
  createdBy: string;
  createdDate: string;
}

const formatUsageDateTime = (dateValue?: string) => {
  if (!dateValue) {
    return "";
  }

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export const useGetDemoKitUsage = (demoKitId: string | null, enabled = false) =>
  useQuery<DemoKitUsageRecord[], ApiServiceErr>(
    ["demoKitUsage", demoKitId],
    async () => {
      const response = await axiosApi.get(`/recipe/demo-kit/${demoKitId}/usage`);
      const data = response?.data;
      const records = Array.isArray(data?.recipes) ? data.recipes : Array.isArray(data) ? data : [];

      return records.map((record: DemoKitUsageItem) => ({
        recipeName: record.recipeName ?? "",
        createdBy:
          record.userFirstName || record.userLastName
            ? `${record.userFirstName} ${record.userLastName}`
            : "",
        createdDate: formatUsageDateTime(record.copiedFromDemoKitAt ?? record.createdAt)
      }));
    },
    { enabled: enabled && !!demoKitId }
  );

export const useGetDemoKitRecipes = (
  pageNumber: number,
  pageSize: number,
  searchKey: string,
  enabled = true
) =>
  useQuery<DemoKitOption[], ApiServiceErr>(
    ["demoKitRecipes", pageNumber, pageSize, searchKey],
    async () => {
      const response = await axiosApi.get("/recipe/demo-kits", {
        params: {
          pageNumber,
          pageSize,
          searchKey
        }
      });
      const recipes = response?.data?.result ?? [];

      return recipes.map((recipe: DemoKitRecipe) => ({
        id: recipe.demoKitId,
        title: recipe.recipeName,
        value: recipe.demoKitId
      }));
    },
    { enabled }
  );

interface UpdateRecipeTypeDto {
  recipeId: string;
  recipeTypeClassification: RecipeTypeClassification;
}

export const useUpdateRecipeType = (
  opt?: UseMutationOptions<void, ApiServiceErr, UpdateRecipeTypeDto>
) =>
  useMutation<void, ApiServiceErr, UpdateRecipeTypeDto>(
    async ({ recipeId, recipeTypeClassification }) => {
      await axiosApi.patch(`/recipe/${recipeId}/type`, {
        recipeTypeClassification
      });
    },
    { ...opt }
  );

export const useGetRecipeById = (recipeId: string | undefined) =>
  useQuery<RecipeData | null, ApiServiceErr>(
    ["recipe", recipeId],
    async () => {
      const response = await axiosApi.get(`/recipe/${recipeId}`);
      return response.data || null;
    },
    {
      enabled: !!recipeId,
      cacheTime: 0
    }
  );

export interface HistoryChange {
  field: string;
  oldValue?: any;
  newValue?: any;
}

export interface RecipeHistoryItem {
  id: string;
  operation: "create" | "update" | "delete";
  changedAt: string;
  changedBy?: {
    id: string;
    name: string;
    email: string;
  };
  changes?: HistoryChange[];
}

export type RecipeHistory = RecipeHistoryItem[];

export interface RecipeHistoryResponse {
  result: RecipeHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
}

export const useGetRecipeHistory = (
  recipeId: string | undefined,
  pageNumber: number = 1,
  pageSize: number = 10
) =>
  useQuery<RecipeHistoryResponse | null, ApiServiceErr>(
    ["recipeHistory", recipeId, pageNumber, pageSize],
    async () => {
      const response = await axiosApi.get(`/recipe/${recipeId}/history`, {
        params: {
          pageNumber,
          pageSize,
        },
      });
      return response.data || null;
    },
    {
      enabled: !!recipeId,
      cacheTime: 0,
    }
  );

export const useUpdateRecipe = (
  opt?: UseMutationOptions<CreateRecipeDto, ApiServiceErr, UpdateRecipe>
) =>
  useMutation<CreateRecipeDto, ApiServiceErr, UpdateRecipe>(
    async ({ recipeId, recipe }) => {
      const response = await axiosApi.put(`/recipe/${recipeId}`, recipe);
      successMessage("Recipe updated successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useDeleteRecipe = (opt?: UseMutationOptions<void, ApiServiceErr, string>) => {
  return useMutation<void, ApiServiceErr, string>(
    async (recipeId: string) => {
      const response = await axiosApi.delete(`/recipe/${recipeId}`);
      successMessage("Recipe deleted successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );
};

export const useCalculateRecipe = (
  opt?: UseMutationOptions<RecipeResult, ApiServiceErr, { recipeId: string; payload: Recipe }>
) =>
  useMutation<RecipeResult, ApiServiceErr, { recipeId: string; payload: Recipe }>(
    async ({ recipeId, payload }) => {
      const url = `/recipe/queue/${recipeId}`;
      const response = await axiosApi.put<RecipeResult>(url, payload);
      successMessage("Recipe calculated successfully.");
      return response.data;
    },
    { ...opt }
  );

export const useAddRecipeIngredient = (
  opt?: UseMutationOptions<Recipe, ApiServiceErr, AddOriginalIngredientDto>
) =>
  useMutation<Recipe, ApiServiceErr, AddOriginalIngredientDto>(
    async data => {
      const response = await axiosApi.post(`/recipe/${data.recipeId}/ingredients`, data);
      successMessage("Ingredient added successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useUpdateOriginalIngredient = (
  opt?: UseMutationOptions<Recipe, ApiServiceErr, UpdateOriginalIngredientDto>
) =>
  useMutation<Recipe, ApiServiceErr, UpdateOriginalIngredientDto>(
    async data => {
      const response = await axiosApi.put(
        `/recipe/${data.recipeId}/original/ingredients/${data.id}`,
        data
      );
      successMessage("Ingredient updated successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useDeleteRecipeIngredient = (
  opt?: UseMutationOptions<Recipe, ApiServiceErr, DeleteRecipeIngredientDto>
) =>
  useMutation<Recipe, ApiServiceErr, DeleteRecipeIngredientDto>(
    async data => {
      const response = await axiosApi.delete(`/recipe/${data.recipeId}/ingredients/${data.id}`, {
        data
      });
      successMessage("Ingredient deleted successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useUpdateIngredientPercentage = (
  opt?: UseMutationOptions<Recipe, ApiServiceErr, UpdateIngredientPercentageDto>
) =>
  useMutation<Recipe, ApiServiceErr, UpdateIngredientPercentageDto>(
    async data => {
      const response = await axiosApi.patch(`/recipe/${data.recipeId}/ingredient-percentage`, data);
      return response.data;
    },
    {
      ...opt
    }
  );

export const useCreateOriginalRecipeProcess = (
  recipeId: string,
  opt?: UseMutationOptions<void, ApiServiceErr, AddProcess>
) =>
  useMutation<void, ApiServiceErr, AddProcess>(
    async data => {
      const payload: AddProcess = {
        ...data,
        recipeId,
        variantId: ""
      };
      const response = await axiosApi.post(`/recipe/${recipeId}/original/process`, payload);
      successMessage("Original recipe process created successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useCreateVariantProcess = (
  recipeId: string,
  variantId: string,
  opt?: UseMutationOptions<void, ApiServiceErr, AddProcess>
) =>
  useMutation<void, ApiServiceErr, AddProcess>(
    async data => {
      const payload: AddProcess = {
        ...data,
        recipeId,
        variantId
      };
      const response = await axiosApi.post(
        `/recipe/${data.recipeId}/variant/${variantId}/process`,
        payload
      );
      successMessage("Variant recipe process created successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useUpdateRecipeProcess = (
  opt?: UseMutationOptions<void, ApiServiceErr, UpdateProcess>
) =>
  useMutation<void, ApiServiceErr, UpdateProcess>(
    async (data: UpdateProcess) => {
      const { recipeId, processId, variantId, recipeType } = data;
      const isOriginal = recipeType === RecipeType.ORIGINAL;

      const url = isOriginal
        ? `/recipe/${recipeId}/original/process/${processId}`
        : `/recipe/${recipeId}/variant/${variantId}/process/${processId}`;

      const response = await axiosApi.put(url, data);
      successMessage(`${isOriginal ? "Baseline" : "Variant"} recipe process updated successfully.`);
      return response.data;
    },
    { ...opt }
  );

export const useDeleteRecipeProcess = (
  opt?: UseMutationOptions<void, ApiServiceErr, DeleteProcess>
) =>
  useMutation<void, ApiServiceErr, DeleteProcess>(
    async ({ recipeId, processId, variantId, recipeType }) => {
      const isOriginal = recipeType === RecipeType.ORIGINAL;

      const url = isOriginal
        ? `/recipe/${recipeId}/original/process/${processId}`
        : `/recipe/${recipeId}/variant/${variantId}/process/${processId}`;

      const response = await axiosApi.delete(url);
      successMessage(`${isOriginal ? "Original" : "Variant"} recipe process deleted successfully.`);
      return response.data;
    },
    { ...opt }
  );

export const useGetVariantById = (recipeId: string, variantId: string) =>
  useQuery<Variant | null, ApiServiceErr>(
    ["recipe", recipeId, "variant", variantId],
    async () => {
      const response = await axiosApi.get(`/recipe/${recipeId}/variant/${variantId}`);
      return response.data || null;
    },
    {
      enabled: !!recipeId && !!variantId,
      cacheTime: 0
    }
  );

export const useAddVariant = (opt?: UseMutationOptions<Recipe, ApiServiceErr, AddVariant>) =>
  useMutation<Recipe, ApiServiceErr, AddVariant>(
    async ({ recipeId, variant }) => {
      const response = await axiosApi.post(`/recipe/${recipeId}/variant`, variant);
      successMessage("Variant added successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useUpdateVariant = (opt?: UseMutationOptions<Variant, ApiServiceErr, UpdateVariant>) =>
  useMutation<Variant, ApiServiceErr, UpdateVariant>(
    async ({ recipeId, variantId, variant }) => {
      const response = await axiosApi.put(`/recipe/${recipeId}/variant/${variantId}`, variant);
      successMessage("Variant updated successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );

export const useGetAvailableProcesses = (recipeId: string, variantId: string | undefined) =>
  useQuery<Process[] | null, ApiServiceErr>(
    ["recipe", recipeId, variantId, "available-processes"],
    async () => {
      const response = await axiosApi.get(`/recipe/${recipeId}/${variantId}/available-processes`);
      return response.data || null;
    },
    {
      enabled: !!recipeId && !!variantId,
      cacheTime: 0
    }
  );

export const useDeleteRecipeVariant = (opt?: any) =>
  useMutation(
    async (data: any) => {
      const response = await axiosApi.delete(`/recipe/${data.recipeId}/variant`, {
        data: data.ids
      });
      successMessage("Variant deleted successfully.");
      return response.data;
    },
    {
      ...opt
    }
  );